<template>
  <b-card
    id="order-price-limit-settings"
    class="order-price-limit-card"
  >
    <template #header>
      <div class="card-heading">
        <i class="fas fa-shield-alt" />
        <h4>Plafond TTC des commandes</h4>
      </div>
    </template>

    <p class="description">
      Définissez, pour chaque société, le montant TTC maximum accepté lors de
      la création ou de l'import de commandes.
    </p>

    <b-form @submit.prevent="saveLimit">
      <b-form-group
        label="Société"
        label-for="order-limit-company"
      >
        <b-form-select
          id="order-limit-company"
          v-model="selectedCompanyId"
          :options="companyOptions"
          :disabled="loading || companies.length === 0"
          @change="loadSelectedCompanyLimit"
        />
      </b-form-group>

      <b-form-group
        label="Montant TTC maximum par commande"
        label-for="max-order-price-ttc"
      >
        <b-input-group append="TND">
          <b-form-input
            id="max-order-price-ttc"
            v-model="maxOrderPriceTtc"
            type="text"
            inputmode="decimal"
            placeholder="999.000 (vide = sans limite)"
            :formatter="formatPrice"
            :state="isLimitValid ? null : false"
            :disabled="loading || !selectedCompanyId"
            @keypress="allowOnlyDecimalNumbers"
          />
        </b-input-group>
        <small class="text-muted">
          Laissez vide pour autoriser tous les montants. Sinon, la valeur doit être supérieure ou égale à 0.001 TND.
        </small>
      </b-form-group>

      <div class="actions">
        <b-button
          type="submit"
          variant="primary"
          :disabled="loading || !selectedCompanyId || !isLimitValid"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-2"
          />
          <i
            v-else
            class="fas fa-save mr-2"
          />
          Enregistrer
        </b-button>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import axios from 'axios'
import {
  BButton,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'OrderPriceLimitSettings',
  components: {
    BButton,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BSpinner,
  },
  data() {
    return {
      companies: [],
      selectedCompanyId: null,
      maxOrderPriceTtc: '',
      loading: false,
    }
  },
  computed: {
    companyOptions() {
      return [
        { value: null, text: 'Sélectionner une société', disabled: true },
        ...this.companies.map(company => ({
          value: company.id,
          text: company.name || `Société #${company.id}`,
        })),
      ]
    },
    isLimitValid() {
      if (String(this.maxOrderPriceTtc).trim() === '') return true
      const value = Number(this.maxOrderPriceTtc)
      return Number.isFinite(value) && value >= 0.001
    },
  },
  created() {
    this.fetchCompanies()
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''
      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }
      if (isDecimalSeparator && (currentValue.includes('.') || currentValue.includes(','))) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const separatorIndex = cleaned.indexOf('.')
      if (separatorIndex !== -1) {
        cleaned = cleaned.slice(0, separatorIndex + 1)
          + cleaned.slice(separatorIndex + 1).replace(/\./g, '').slice(0, 3)
      }
      return cleaned
    },
    loadSelectedCompanyLimit() {
      const company = this.companies.find(item => item.id === this.selectedCompanyId)
      this.maxOrderPriceTtc = company?.max_order_price_ttc === null
        || company?.max_order_price_ttc === undefined
        ? ''
        : String(company.max_order_price_ttc)
    },
    async fetchCompanies() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/companies/order-price-limits/')
        this.companies = Array.isArray(data) ? data : (data.results || [])
        if (this.companies.length > 0) {
          this.selectedCompanyId = this.companies[0].id
          this.loadSelectedCompanyLimit()
        }
      } catch (error) {
        this.$bvToast.toast('Impossible de charger les plafonds TTC', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loading = false
      }
    },
    async saveLimit() {
      if (!this.selectedCompanyId || !this.isLimitValid) return

      this.loading = true
      try {
        const value = String(this.maxOrderPriceTtc).trim() === ''
          ? null
          : Number(this.maxOrderPriceTtc).toFixed(3)
        const { data } = await axios.patch(
          `/api/companies/order-price-limits/${this.selectedCompanyId}/`,
          { max_order_price_ttc: value },
        )
        const index = this.companies.findIndex(company => company.id === data.id)
        if (index !== -1) this.$set(this.companies, index, data)
        this.maxOrderPriceTtc = data.max_order_price_ttc === null
          || data.max_order_price_ttc === undefined
          ? ''
          : String(data.max_order_price_ttc)
        this.$bvToast.toast('Plafond TTC enregistré avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        const message = error.response?.data?.max_order_price_ttc?.[0]
          || 'Impossible d\'enregistrer le plafond TTC'
        this.$bvToast.toast(message, { title: 'Erreur', variant: 'danger' })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.order-price-limit-card {
  margin-bottom: 2rem;
  border: 0;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(31, 45, 61, 0.08);
}

.card-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-heading h4 {
  margin: 0;
  font-weight: 600;
}

.card-heading i {
  color: #7367f0;
}

.description {
  margin-bottom: 1.5rem;
  color: #6e6b7b;
}

.actions {
  display: flex;
  justify-content: flex-end;
}
</style>
